import React from 'react';
import { useNavigate } from 'react-router-dom';
import InlineLayout from '@/shared/layout/inlineLayout';
import Loader from '@/app/routes/Loader';
import { Tooltip } from '@/shared/components/Tooltip';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import {
  useViewFacility,
  FACILITY_PROFILE_NAV_ITEMS,
} from '../../hooks/useViewFacility';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import { FACILITY_EDIT_ERROR_MESSAGES } from '../../errors/errorMessages';
import ViewFacilityBasicInfo from './ViewFacilityBasicInfo';
import { ViewFacilityEquipment } from './ViewFacilityEquipment';
import ViewFacilityUsers from './ViewFacilityUsers';
import ViewFacilityErrorScreen from './ViewFacilityErrorScreen';

interface ViewFacilityPageProps {
  facilityId: string;
}

const ViewFacilityPage: React.FC<ViewFacilityPageProps> = ({ facilityId }) => {
  const navigate = useNavigate();
  const { privileges } = useUserPrivileges();

  const {
    facility,
    isLoading,
    showErrorScreen,
    statusLabel,
    statusBadgeClasses,
  } = useViewFacility(facilityId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  const profileHeader = (
    <>
      <div
        className="flex items-center justify-center shrink-0 rounded-full bg-[#E8EEF0] text-[#223746] font-medium overflow-hidden"
        style={{ width: '64px', height: '64px', fontSize: '20px' }}
      >
        {facility?.name
          ?.trim()
          .split(/\s+/)
          .slice(0, 2)
          .map((w) => w[0] ?? '')
          .join('')
          .toUpperCase() ?? ''}
      </div>

      <div className="flex min-w-0 flex-1">
        <div className="flex min-w-0 flex-col gap-[4px]">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h2
              className="min-w-0 break-all font-medium text-neutral-10"
              style={{ fontSize: '18px', lineHeight: '28px' }}
            >
              {facility?.name ?? '—'}
            </h2>
            <span
              className={`px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${statusBadgeClasses.container}`}
              style={{ fontSize: '14px', lineHeight: '20px' }}
            >
              <div
                className={`rounded-full shrink-0 ${statusBadgeClasses.dot}`}
                style={{ width: '6px', height: '6px' }}
              />
              {statusLabel}
            </span>
          </div>
        </div>
      </div>

      {privileges.canEditFacility ? (
        <button
          data-testid="Edycja placówki"
          onClick={() => navigate(`/Placówki/${facilityId}/Edycja-placówki`)}
          className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] mt-1 shrink-0"
        >
          <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
            Edycja placówki
          </span>
        </button>
      ) : (
        <Tooltip
          content={FACILITY_EDIT_ERROR_MESSAGES.ACCESS_DENIED_TOOLTIP}
          forceShow
          side="bottom"
          contentMaxWidth={320}
          triggerWrapContent
        >
          <button
            disabled
            className="h-10 bg-[#2A4455] opacity-40 cursor-not-allowed px-4 py-2.5 rounded-lg flex items-center justify-center focus:outline-none mt-1 shrink-0"
          >
            <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
              Edycja placówki
            </span>
          </button>
        </Tooltip>
      )}
    </>
  );

  return (
    <React.Fragment key={facilityId}>
      {showErrorScreen ? (
        <ViewFacilityErrorScreen />
      ) : (
        <InlineLayout
          title={FACILITY_LABELS.VIEW_TITLE}
          navItems={[...FACILITY_PROFILE_NAV_ITEMS]}
          contentByNavId={{
            BasicInformation: (
              <ViewFacilityBasicInfo facility={facility ?? null} />
            ),
            Equipment: <ViewFacilityEquipment facilityId={facilityId} />,
            Users: <ViewFacilityUsers facilityId={facilityId} />,
          }}
          header={profileHeader}
          searchParamKey="tab"
          defaultActiveId="BasicInformation"
        />
      )}
    </React.Fragment>
  );
};

export default ViewFacilityPage;
